import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/lib/firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';

const ClientContext = createContext();

export const useClient = () => useContext(ClientContext);

export const ClientProvider = ({ children }) => {
  const { userDetails } = useAuth();
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true); // To handle loading state
  const location = useLocation();
  const navigate = useNavigate();

  // List of clients the user has access to
  const availableClients = Object.keys(userDetails?.clientPermissions || {}).map(clientId => ({
    id: clientId,
    name: userDetails.clientPermissions[clientId]?.clientName || 'Unknown',
  }));

  // Sync selected client with the query string in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientIdFromURL = params.get('clientId');

    if (!selectedClient && availableClients.length > 0) {
      const internalClient = availableClients.find(client => client.name === 'Internal');
      const defaultClient = internalClient || availableClients[0];

      // If the URL has a clientId, select the corresponding client
      const clientFromUrl = availableClients.find(client => client.id === clientIdFromURL);

      // Set selected client and sync the URL if necessary
      setSelectedClient(clientFromUrl || defaultClient);

      // If the URL clientId does not match, sync it with the default client
      if (!clientIdFromURL || !clientFromUrl) {
        params.set('clientId', defaultClient.id);
        navigate({ search: params.toString() }, { replace: true });
      }

      setLoading(false); // Done loading once the client is set
    } else if (selectedClient) {
      setLoading(false); // If already selected, we're not loading anymore
    }
  }, [availableClients, selectedClient, location.search, navigate]);

  const selectClient = (clientId) => {
    const client = availableClients.find(client => client.id === clientId);
    setSelectedClient(client || null);

    // Update the URL with the selected client ID
    const params = new URLSearchParams(location.search);
    params.set('clientId', clientId);
    navigate({ search: params.toString() });
  };

  return (
    <ClientContext.Provider value={{ selectedClient, availableClients, selectClient, loading }}>
      {children}
    </ClientContext.Provider>
  );
};
